import React, { useState, useEffect } from "react";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  PaymentRequestButtonElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { MdArrowBack, MdCheckCircle, MdRemove, MdVolunteerActivism } from "react-icons/md";
import { FaApplePay, FaGooglePay } from "react-icons/fa";
import AlertPane from "components/alert";
import Button, { IconButton } from "components/button";
import Dialog from "components/dialog";
import { Checkbox, FormField, InputField, Select, getControlClassName } from "components/fields";
import { SegmentedControl } from "components/tabs";
import { formatCurrency } from "utils/format";
import { getLinkHubTheme } from "../theme";

const BASE_URL = process.env.REACT_APP_BASE_URL || "http://localhost:8080";

// Initialize Stripe
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
    "pk_test_51QAKyiK4r9HYqz3Y6X3K4r9HYqz3Y6X3K4r9HYqz3Y6X3K4r9HY"
);

// Calculate processing fee (2.9% + $0.30 standard Stripe fee)
const calculateFee = (amount) => {
  const fee = Math.round(amount * 0.029 + 30); // in cents
  return fee;
};

const formatCents = (cents) => formatCurrency(cents / 100);

const DONATION_TYPE_OPTIONS = [
  { value: "one-time", label: "One-Time" },
  { value: "recurring", label: "Recurring" },
];

const FREQUENCY_OPTIONS = [
  { value: "week", label: "Weekly" },
  { value: "month", label: "Monthly" },
  { value: "year", label: "Yearly" },
];

// Tenant-accented toggle tile (fund and frequency choices).
const choiceProps = (selected, theme) => ({
  "aria-pressed": selected,
  className: `border-2 transition focus-ring ${selected ? "" : "border-line text-fg hover:bg-surface-subtle"}`,
  style: selected
    ? {
        borderColor: theme.accent,
        backgroundColor: theme.accentTint,
        color: theme.accent,
        borderRadius: theme.radius,
      }
    : { borderRadius: theme.radius },
});

// Payment Request Button Component (Apple Pay / Google Pay)
const PaymentRequestButton = ({
  amount,
  allocations,
  coverFees,
  donorEmail,
  donorName,
  slug,
  onSuccess,
  onError,
  onTrackEvent,
}) => {
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [canMakePayment, setCanMakePayment] = useState(false);

  const totalAmount = coverFees ? amount + calculateFee(amount) : amount;

  // Get label for payment request
  const getLabel = () => {
    if (allocations.length === 1) {
      return allocations[0].fundName || "Donation";
    }
    return `Donation (${allocations.length} funds)`;
  };

  useEffect(() => {
    if (!stripe || !amount) return;

    const pr = stripe.paymentRequest({
      country: "US",
      currency: "usd",
      total: {
        label: getLabel(),
        amount: totalAmount,
      },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    pr.canMakePayment().then((result) => {
      if (result) {
        setPaymentRequest(pr);
        setCanMakePayment(true);
      }
    });

    pr.on("paymentmethod", async (event) => {
      try {
        // Create payment intent on the server
        const response = await axios.post(
          `${BASE_URL}/api/connect/public/${slug}/donate`,
          {
            amount: amount / 100, // Convert to dollars for API
            donorEmail: event.payerEmail || donorEmail,
            donorName: event.payerName || donorName,
            allocations: allocations.map(a => ({
              fundId: a.fundId,
              amount: a.amount / 100, // Convert to dollars
            })),
            coverFees,
            feeAmount: coverFees ? calculateFee(amount) / 100 : 0,
            paymentMethod: event.walletName === "applePay" ? "apple_pay" : "google_pay",
          }
        );

        if (!response.data.success) {
          event.complete("fail");
          onError("Failed to create payment");
          return;
        }

        const { clientSecret } = response.data.data;

        // Confirm the payment with the payment method from Apple/Google Pay
        const { error, paymentIntent } = await stripe.confirmCardPayment(
          clientSecret,
          { payment_method: event.paymentMethod.id },
          { handleActions: false }
        );

        if (error) {
          event.complete("fail");
          onError(error.message);
        } else if (paymentIntent.status === "succeeded") {
          event.complete("success");
          if (onTrackEvent) onTrackEvent("donation_completed");
          onSuccess();
        } else if (paymentIntent.status === "requires_action") {
          event.complete("success");
          const { error: confirmError } = await stripe.confirmCardPayment(clientSecret);
          if (confirmError) {
            onError(confirmError.message);
          } else {
            if (onTrackEvent) onTrackEvent("donation_completed");
            onSuccess();
          }
        }
      } catch (err) {
        event.complete("fail");
        onError(err.message || "Payment failed");
      }
    });
  }, [stripe, amount, totalAmount, allocations, coverFees, donorEmail, donorName, slug, onSuccess, onError, onTrackEvent]);

  // Update payment request amount when it changes
  useEffect(() => {
    if (paymentRequest && amount) {
      paymentRequest.update({
        total: {
          label: getLabel(),
          amount: totalAmount,
        },
      });
    }
  }, [paymentRequest, amount, totalAmount, allocations]);

  if (!canMakePayment || !paymentRequest) {
    return null;
  }

  return (
    <div className="mb-4">
      <PaymentRequestButtonElement
        options={{
          paymentRequest,
          style: {
            paymentRequestButton: {
              type: "donate",
              theme: "dark",
              height: "48px",
            },
          },
        }}
      />
      <div className="mt-3 flex items-center justify-center gap-2 text-xs text-fg-muted">
        <FaApplePay aria-hidden="true" className="h-5 w-auto" />
        <span>or</span>
        <FaGooglePay aria-hidden="true" className="h-5 w-auto" />
        <span>available</span>
      </div>
      <div className="relative my-4">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-line" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="bg-surface px-3 text-fg-muted">or pay with card</span>
        </div>
      </div>
    </div>
  );
};

// Payment Form Component
const PaymentForm = ({
  onSuccess,
  onError,
  donationType,
  amount,
  allocations,
  coverFees,
  donorEmail,
  donorName,
  slug,
  onTrackEvent,
  theme,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);

    try {
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: "if_required",
      });

      if (error) {
        onError(error.message);
      } else {
        onSuccess();
      }
    } catch (err) {
      onError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentRequestButton
        amount={amount}
        allocations={allocations}
        coverFees={coverFees}
        donorEmail={donorEmail}
        donorName={donorName}
        slug={slug}
        onSuccess={onSuccess}
        onError={onError}
        onTrackEvent={onTrackEvent}
      />

      <PaymentElement />
      <Button
        type="submit"
        size="lg"
        fullWidth
        disabled={!stripe}
        loading={processing}
        className="font-semibold"
        style={theme.primaryButtonStyle}
      >
        {processing
          ? "Processing…"
          : donationType === "recurring"
          ? "Start Recurring Donation"
          : "Complete Donation"}
      </Button>
    </form>
  );
};

// Split Giving Allocation Component
const SplitAllocationItem = ({ fund, allocation, onChange, onRemove, canRemove }) => {
  const handleAmountChange = (e) => {
    const value = e.target.value.replace(/[^0-9.]/g, "");
    onChange(fund.id, value);
  };

  return (
    <div className="flex items-center gap-3 rounded-control border border-line p-3">
      <div className="min-w-0 flex-1">
        <p className="font-medium text-fg">{fund.name}</p>
        {fund.description && (
          <p className="line-clamp-1 text-xs text-fg-muted">{fund.description}</p>
        )}
      </div>
      <div className="flex items-center gap-2">
        <div className="relative w-24">
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-fg-muted"
          >
            $
          </span>
          <input
            type="text"
            inputMode="decimal"
            aria-label={`Amount for ${fund.name}`}
            value={allocation || ""}
            onChange={handleAmountChange}
            placeholder="0"
            className={getControlClassName({ className: "pl-6 pr-2 text-right font-medium" })}
          />
        </div>
        {canRemove && (
          <IconButton
            icon={<MdRemove />}
            label={`Remove ${fund.name}`}
            size="sm"
            onClick={() => onRemove(fund.id)}
          />
        )}
      </div>
    </div>
  );
};

const DonationForm = ({ isOpen, onClose, slug, profile, onTrackEvent }) => {
  const [step, setStep] = useState(1); // 1: Amount selection, 2: Payment
  const [donationType, setDonationType] = useState("one-time"); // one-time or recurring
  const [frequency, setFrequency] = useState("month"); // week, month, year
  const [amount, setAmount] = useState("");
  const [customAmount, setCustomAmount] = useState("");
  const [selectedFund, setSelectedFund] = useState(null);
  const [funds, setFunds] = useState([]);
  const [coverFees, setCoverFees] = useState(false);
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const theme = getLinkHubTheme(profile);

  // Split giving state
  const [splitGiving, setSplitGiving] = useState(false);
  const [splitAllocations, setSplitAllocations] = useState({}); // { fundId: amountInDollars }
  const [activeSplitFunds, setActiveSplitFunds] = useState([]); // fund IDs that are active in split

  // Fetch funds when the dialog opens
  useEffect(() => {
    if (!isOpen || !slug) return;

    const fetchFunds = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/connect/public/${slug}/funds`);
        if (response.data.success && response.data.data) {
          const activeFunds = response.data.data;
          setFunds(activeFunds);
          const defaultFund = activeFunds.find((f) => f.isDefault) || activeFunds[0];
          if (defaultFund) {
            setSelectedFund(defaultFund);
            // Initialize split with default fund
            setActiveSplitFunds([defaultFund.id]);
          }
        }
      } catch (err) {
        console.error("Error fetching funds:", err);
      }
    };

    fetchFunds();
  }, [isOpen, slug]);

  const handleCustomAmountChange = (e) => {
    const value = e.target.value.replace(/[^0-9.]/g, "");
    setCustomAmount(value);
    setAmount(value);
  };

  // Split giving handlers
  const handleSplitAllocationChange = (fundId, value) => {
    setSplitAllocations((prev) => ({
      ...prev,
      [fundId]: value,
    }));
  };

  const handleAddSplitFund = (fundId) => {
    if (!activeSplitFunds.includes(fundId)) {
      setActiveSplitFunds((prev) => [...prev, fundId]);
    }
  };

  const handleRemoveSplitFund = (fundId) => {
    setActiveSplitFunds((prev) => prev.filter((id) => id !== fundId));
    setSplitAllocations((prev) => {
      const newAllocations = { ...prev };
      delete newAllocations[fundId];
      return newAllocations;
    });
  };

  // Calculate split total
  const splitTotal = Object.values(splitAllocations).reduce(
    (sum, val) => sum + (parseFloat(val) || 0),
    0
  );

  // Calculate total with fees
  const donationAmountCents = splitGiving
    ? Math.round(splitTotal * 100)
    : Math.round(parseFloat(amount || 0) * 100);
  const feeAmount = coverFees ? calculateFee(donationAmountCents) : 0;
  const totalAmountCents = donationAmountCents + feeAmount;

  // Build allocations array for API
  const buildAllocations = () => {
    if (splitGiving) {
      return activeSplitFunds
        .filter((fundId) => splitAllocations[fundId] && parseFloat(splitAllocations[fundId]) > 0)
        .map((fundId) => {
          const fund = funds.find((f) => f.id === fundId);
          return {
            fundId,
            fundName: fund?.name,
            amount: Math.round(parseFloat(splitAllocations[fundId]) * 100),
          };
        });
    }
    // Single fund
    return selectedFund
      ? [{
          fundId: selectedFund.id,
          fundName: selectedFund.name,
          amount: donationAmountCents,
        }]
      : [];
  };

  const handleContinue = async () => {
    const donationAmount = splitGiving ? splitTotal : parseFloat(amount || customAmount);

    if (!donationAmount || donationAmount < 1) {
      setError("Please enter a valid amount ($1 minimum)");
      return;
    }

    if (!donorEmail) {
      setError("Please provide your email address");
      return;
    }

    // Validate split allocations
    if (splitGiving) {
      const allocations = buildAllocations();
      if (allocations.length === 0) {
        setError("Please allocate at least one fund");
        return;
      }
    }

    setLoading(true);
    setError("");

    try {
      const allocations = buildAllocations();
      let response;

      if (donationType === "recurring") {
        response = await axios.post(
          `${BASE_URL}/api/connect/public/${slug}/donate/recurring`,
          {
            amount: donationAmount,
            frequency,
            donorEmail,
            donorName: donorName || "Anonymous",
            allocations,
            fundId: allocations.length === 1 ? allocations[0].fundId : null,
            coverFees,
            feeAmount: coverFees ? feeAmount / 100 : 0,
          }
        );
      } else {
        response = await axios.post(
          `${BASE_URL}/api/connect/public/${slug}/donate`,
          {
            amount: donationAmount,
            donorEmail,
            donorName: donorName || "Anonymous",
            allocations,
            fundId: allocations.length === 1 ? allocations[0].fundId : null,
            coverFees,
            feeAmount: coverFees ? feeAmount / 100 : 0,
          }
        );
      }

      if (response.data.success) {
        setClientSecret(response.data.data.clientSecret);
        if (onTrackEvent) {
          onTrackEvent("donation_started");
        }
        setStep(2);
      }
    } catch (err) {
      console.error("Error creating payment intent:", err);
      setError(
        err.response?.data?.message ||
          "Failed to initialize payment. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = () => {
    setSuccess(true);
    if (onTrackEvent) {
      onTrackEvent("donation_completed");
    }
    setTimeout(() => {
      handleClose();
    }, 3000);
  };

  const handlePaymentError = (errorMessage) => {
    setError(errorMessage);
  };

  const handleClose = () => {
    setStep(1);
    setDonationType("one-time");
    setAmount("");
    setCustomAmount("");
    setDonorName("");
    setDonorEmail("");
    setClientSecret("");
    setSuccess(false);
    setError("");
    setCoverFees(false);
    setSplitGiving(false);
    setSplitAllocations({});
    onClose();
  };

  // Toggle split giving mode
  const handleToggleSplitGiving = () => {
    if (!splitGiving) {
      // Entering split mode - initialize with current amount if set
      if (selectedFund && amount) {
        setSplitAllocations({ [selectedFund.id]: amount });
        setActiveSplitFunds([selectedFund.id]);
      }
    } else {
      // Exiting split mode - set amount to split total if any
      if (splitTotal > 0) {
        setAmount(splitTotal.toString());
        setCustomAmount(splitTotal.toString());
      }
    }
    setSplitGiving(!splitGiving);
  };

  const allocations = buildAllocations();
  const availableFundsToAdd = funds.filter((f) => !activeSplitFunds.includes(f.id));
  const enteredAmount = customAmount || amount;

  const title = (
    <span className="flex items-center gap-3">
      {profile?.logoUrl ? (
        <img src={profile.logoUrl} alt="" className="h-10 w-10 shrink-0 rounded-full object-cover" />
      ) : (
        <span
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: theme.accentTint, color: theme.accent }}
        >
          <MdVolunteerActivism aria-hidden="true" className="h-5 w-5" />
        </span>
      )}
      <span className="min-w-0">
        <span className="block" style={{ fontFamily: theme.headingFont, color: theme.heading }}>
          {profile?.givingButtonText || "Give"}
        </span>
        <span
          className="block text-sm font-normal"
          style={{ fontFamily: theme.bodyFont, color: theme.body }}
        >
          {profile?.displayName}
        </span>
      </span>
    </span>
  );

  return (
    <Dialog isOpen={isOpen} onClose={handleClose} title={title}>
      <div style={{ fontFamily: theme.bodyFont }}>
        {success ? (
          <div className="py-12 text-center">
            <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-success-subtle text-success-fg">
              <MdCheckCircle aria-hidden="true" className="h-12 w-12" />
            </div>
            <h3 className="mb-2 text-heading">Thank You!</h3>
            <p className="text-sm text-fg-muted">
              Your {donationType === "recurring" ? "recurring " : ""}donation of{" "}
              {formatCents(totalAmountCents)} has been processed successfully.
            </p>
            {allocations.length === 1 && allocations[0].fundName && (
              <p className="mt-2 text-sm text-fg-muted">
                Designated to: {allocations[0].fundName}
              </p>
            )}
            {allocations.length > 1 && (
              <div className="mt-3 text-sm text-fg-muted">
                <p className="font-medium">Split across:</p>
                {allocations.map((a) => (
                  <p key={a.fundId}>{a.fundName}: {formatCents(a.amount)}</p>
                ))}
              </div>
            )}
          </div>
        ) : step === 1 ? (
          <div className="space-y-5">
            {error && (
              <AlertPane tone="danger" className="">
                {error}
              </AlertPane>
            )}

            {/* Fund Selection - Single or Split */}
            {funds.length > 0 && (
              <div role="group" aria-labelledby="link-hub-fund-label">
                <div className="mb-1.5 flex items-center justify-between">
                  <p id="link-hub-fund-label" className="text-sm font-medium text-fg">
                    {splitGiving ? "Split Your Gift" : "Select Fund"}
                  </p>
                  {funds.length > 1 && (
                    <button
                      type="button"
                      onClick={handleToggleSplitGiving}
                      className="rounded-sm text-sm font-medium hover:underline focus-ring"
                      style={{ color: theme.accent }}
                    >
                      {splitGiving ? "Single Fund" : "Split Gift"}
                    </button>
                  )}
                </div>

                {splitGiving ? (
                  <div className="space-y-2">
                    {activeSplitFunds.map((fundId) => {
                      const fund = funds.find((f) => f.id === fundId);
                      if (!fund) return null;
                      return (
                        <SplitAllocationItem
                          key={fundId}
                          fund={fund}
                          allocation={splitAllocations[fundId]}
                          onChange={handleSplitAllocationChange}
                          onRemove={handleRemoveSplitFund}
                          canRemove={activeSplitFunds.length > 1}
                        />
                      );
                    })}

                    {availableFundsToAdd.length > 0 && (
                      <Select
                        aria-label="Add another fund"
                        value=""
                        placeholder="Add another fund"
                        onChange={(e) => {
                          if (e.target.value) handleAddSplitFund(e.target.value);
                        }}
                        options={availableFundsToAdd.map((fund) => ({
                          value: fund.id,
                          label: fund.name,
                        }))}
                      />
                    )}

                    <div className="mt-3 flex items-center justify-between rounded-control bg-surface-subtle px-4 py-2">
                      <span className="text-sm font-medium text-fg-muted">Total</span>
                      <span
                        className={`text-lg font-bold ${splitTotal > 0 ? "" : "text-fg-muted"}`}
                        style={splitTotal > 0 ? { color: theme.accent } : undefined}
                      >
                        {formatCents(splitTotal * 100)}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {funds.map((fund) => {
                      const { className, style, ...rest } = choiceProps(selectedFund?.id === fund.id, theme);
                      return (
                        <button
                          key={fund.id}
                          type="button"
                          onClick={() => setSelectedFund(fund)}
                          className={`${className} px-4 py-3 text-left`}
                          style={style}
                          {...rest}
                        >
                          <span className="block font-medium">{fund.name}</span>
                          {fund.description && (
                            <span className="mt-0.5 line-clamp-1 block text-xs text-fg-muted">
                              {fund.description}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <div>
              <p className="mb-1.5 text-sm font-medium text-fg">Donation Type</p>
              <SegmentedControl
                aria-label="Donation Type"
                value={donationType}
                onChange={setDonationType}
                options={DONATION_TYPE_OPTIONS}
                fullWidth
              />
            </div>

            {donationType === "recurring" && (
              <div role="group" aria-labelledby="link-hub-frequency-label">
                <p id="link-hub-frequency-label" className="mb-1.5 text-sm font-medium text-fg">
                  Frequency
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {FREQUENCY_OPTIONS.map((opt) => {
                    const { className, style, ...rest } = choiceProps(frequency === opt.value, theme);
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setFrequency(opt.value)}
                        className={`${className} px-3 py-2.5 text-sm font-medium`}
                        style={style}
                        {...rest}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Amount input only in single fund mode */}
            {!splitGiving && (
              <FormField label="Amount">
                {(controlProps) => (
                  <div className="relative">
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-xl font-bold text-fg-muted"
                    >
                      $
                    </span>
                    <input
                      {...controlProps}
                      type="text"
                      inputMode="decimal"
                      value={enteredAmount}
                      onChange={handleCustomAmountChange}
                      placeholder="Enter amount"
                      className={`w-full rounded-control border-2 py-3.5 pl-10 pr-4 text-lg font-bold transition placeholder:text-fg-muted focus-ring ${
                        enteredAmount ? "" : "border-line bg-surface text-fg"
                      }`}
                      style={
                        enteredAmount
                          ? {
                              borderColor: theme.accent,
                              backgroundColor: theme.accentTint,
                              color: theme.accent,
                            }
                          : undefined
                      }
                    />
                  </div>
                )}
              </FormField>
            )}

            {donationAmountCents > 0 && (
              <div
                className={`border-2 p-4 transition ${coverFees ? "" : "border-line"}`}
                style={
                  coverFees
                    ? {
                        borderColor: theme.accent,
                        backgroundColor: theme.accentTint,
                        borderRadius: theme.radius,
                      }
                    : { borderRadius: theme.radius }
                }
              >
                <Checkbox
                  label="Cover processing fees"
                  description={`Add ${formatCents(calculateFee(donationAmountCents))} to cover transaction costs`}
                  checked={coverFees}
                  onChange={(e) => setCoverFees(e.target.checked)}
                />
              </div>
            )}

            <div className="space-y-3">
              <InputField
                label="Email Address"
                type="email"
                autoComplete="email"
                value={donorEmail}
                onChange={(e) => setDonorEmail(e.target.value)}
                required
                placeholder="your@email.com"
              />
              <InputField
                label="Name (optional)"
                autoComplete="name"
                value={donorName}
                onChange={(e) => setDonorName(e.target.value)}
                placeholder="Your name"
              />
            </div>

            {donationAmountCents > 0 && (
              <div className="rounded-control bg-surface-subtle p-4">
                {splitGiving && allocations.length > 1 ? (
                  <>
                    <p className="mb-2 text-sm font-medium text-fg">Allocation</p>
                    {allocations.map((a) => (
                      <div key={a.fundId} className="flex items-center justify-between text-sm">
                        <span className="text-fg-muted">{a.fundName}</span>
                        <span className="font-medium text-fg">{formatCents(a.amount)}</span>
                      </div>
                    ))}
                    <div className="my-2 border-t border-line" />
                  </>
                ) : (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-fg-muted">Donation amount</span>
                    <span className="font-medium text-fg">{formatCents(donationAmountCents)}</span>
                  </div>
                )}
                {coverFees && (
                  <div className="mt-1 flex items-center justify-between text-sm">
                    <span className="text-fg-muted">Processing fee</span>
                    <span className="font-medium text-fg">{formatCents(feeAmount)}</span>
                  </div>
                )}
                <div className="mt-2 flex items-center justify-between border-t border-line pt-2">
                  <span className="font-semibold text-fg">Total</span>
                  <span className="text-lg font-bold" style={{ color: theme.accent }}>
                    {formatCents(totalAmountCents)}
                  </span>
                </div>
                {!splitGiving && selectedFund && (
                  <p className="mt-2 text-xs text-fg-muted">Designated to: {selectedFund.name}</p>
                )}
              </div>
            )}

            <Button
              size="lg"
              fullWidth
              onClick={handleContinue}
              disabled={!donationAmountCents}
              loading={loading}
              className="text-lg font-semibold"
              style={theme.primaryButtonStyle}
            >
              {loading ? "Loading…" : "Continue to Payment"}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-control bg-surface-subtle p-4">
              <div>
                <p className="text-sm text-fg-muted">
                  {donationType === "recurring"
                    ? `${frequency.charAt(0).toUpperCase() + frequency.slice(1)}ly donation`
                    : "One-time donation"}
                </p>
                {allocations.length === 1 && allocations[0].fundName && (
                  <p className="mt-0.5 text-xs text-fg-muted">{allocations[0].fundName}</p>
                )}
                {allocations.length > 1 && (
                  <p className="mt-0.5 text-xs text-fg-muted">
                    Split across {allocations.length} funds
                  </p>
                )}
              </div>
              <p className="text-2xl font-bold" style={{ color: theme.accent }}>
                {formatCents(totalAmountCents)}
              </p>
            </div>

            {error && (
              <AlertPane tone="danger" className="">
                {error}
              </AlertPane>
            )}

            {clientSecret && (
              <Elements
                stripe={stripePromise}
                options={{
                  clientSecret,
                  appearance: {
                    theme: "stripe",
                    variables: {
                      borderRadius: "0.5rem",
                      fontFamily: profile?.bodyFont || "system-ui, sans-serif",
                    },
                  },
                }}
              >
                <PaymentForm
                  onSuccess={handlePaymentSuccess}
                  onError={handlePaymentError}
                  donationType={donationType}
                  amount={donationAmountCents}
                  allocations={allocations}
                  coverFees={coverFees}
                  donorEmail={donorEmail}
                  donorName={donorName}
                  slug={slug}
                  onTrackEvent={onTrackEvent}
                  theme={theme}
                />
              </Elements>
            )}

            <Button
              variant="ghost"
              fullWidth
              leftIcon={<MdArrowBack aria-hidden="true" />}
              onClick={() => {
                setStep(1);
                setClientSecret("");
                setError("");
              }}
            >
              Back to Amount Selection
            </Button>
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default DonationForm;
